import { useState, useEffect, useRef } from "react";
import { toJS } from "mobx";
import { useNavigate } from "react-router-dom";
import CurriculumStore from "../stores/CurriculumStore";

export default function useAlbum(albumId, { curriculumId, courseId }) {
  const navigate = useNavigate();
  const { albumsById, albumDetails } = CurriculumStore;
  const [album, setAlbum] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const loadedAlbumIdRef = useRef(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    // Already loaded or currently fetching
    if (loadedAlbumIdRef.current === albumId || fetchingRef.current) return;

    const matchingAlbumDetails = albumDetails[albumId];
    const matchingAlbum = albumsById?.[albumId];
    if (matchingAlbumDetails && matchingAlbum) {
      setAlbum(toJS(matchingAlbum));
      loadedAlbumIdRef.current = albumId;
      setLoading(false);
    } else {
      if (albumId) {
        setAlbum(undefined);
        loadedAlbumIdRef.current = null;
        fetchingRef.current = true;
        (async () => {
          setLoading(true);
          setError(null);
          setNotFound(false);
          try {
            // Guarantee a completed initialization before fetching details, so a
            // cold load does not race the list/courses fetches.
            await CurriculumStore.ensureInitialized();

            let fetchedAlbumBasics = CurriculumStore?.albumsById?.[albumId];
            let fetchedAlbumDetails = CurriculumStore.albumDetails?.[albumId];

            if (!fetchedAlbumBasics) {
              await CurriculumStore.fetchAlbumList();
              fetchedAlbumBasics = CurriculumStore?.albumsById?.[albumId];
            }

            if (!fetchedAlbumDetails) {
              // Returns the album, or null when it genuinely does not exist (404).
              // A thrown error means the request failed and must NOT be treated as
              // "album not found".
              fetchedAlbumDetails = await CurriculumStore.fetchAlbumDetails(albumId);
            }

            // Treat anything without an id as "album not found" - this also catches
            // APIs that resolve with an empty/error payload instead of throwing.
            const resolvedAlbum =
              CurriculumStore?.albumsById?.[albumId] ??
              (fetchedAlbumBasics?.id ? fetchedAlbumBasics : undefined) ??
              (fetchedAlbumDetails?.id ? fetchedAlbumDetails : undefined);

            if (!resolvedAlbum) {
              // The album does not exist. Redirect to the parent course page - the
              // natural "previous" page in the hierarchy. We can't use navigate(-1)
              // because on a reload/deep-link there is no reliable in-app history
              // entry to pop, so it would no-op or leave the app.
              setNotFound(true);
              loadedAlbumIdRef.current = albumId;
              navigate(`/curriculum/${curriculumId}/courses/${courseId}`, { replace: true });
            } else {
              setAlbum(toJS(resolvedAlbum));
              loadedAlbumIdRef.current = albumId;
            }
          } catch (err) {
            // Fetch failure is not a missing album - do not redirect.
            console.error(err);
            setError(err);
          } finally {
            setLoading(false);
            fetchingRef.current = false;
          }
        })();
      }
    }
  }, [albumId, curriculumId, courseId, reloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (albumDetails[albumId] && albumsById[albumId]) {
      setAlbum(toJS(albumsById[albumId]));
    }
  }, [albumId, albumDetails, albumsById]);

  // Lets an error state retry the load without a full page refresh.
  const reload = () => {
    loadedAlbumIdRef.current = null;
    fetchingRef.current = false;
    setError(null);
    setNotFound(false);
    setReloadNonce(n => n + 1);
  };

  return { album, loading, error, notFound, reload };
}

